import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { navLinks, profile } from "../../data/content";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <motion.header
        data-testid="site-navbar"
        initial={{ x: "-50%", y: -40, opacity: 0 }}
        animate={{ x: "-50%", y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="fixed top-4 md:top-6 left-1/2 z-50 w-[calc(100%-2rem)] max-w-4xl"
      >
        <nav
          className="flex items-center justify-between rounded-full px-4 md:px-5 py-2.5 backdrop-blur-xl transition-colors duration-300"
          style={{
            backgroundColor: scrolled
              ? "var(--glass-bg)"
              : "rgba(10,10,10,0.35)",
            border: "1px solid var(--glass-border)",
            boxShadow: scrolled ? "0 20px 40px -20px rgba(0,0,0,0.8)" : "none",
          }}
        >
          <button
            data-testid="nav-logo"
            onClick={() => handleNav("#top")}
            className="flex items-center gap-2 pl-1 pr-3"
          >
            <span className="font-heading font-extrabold text-sm text-primary-c tracking-tight">
              Utsav Suthar
            </span>
          </button>

          <div className="hidden md:flex items-center gap-7">
            {navLinks.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                onClick={() => handleNav(l.href)}
                className="text-sm font-medium text-secondary-c hover:text-primary-c transition-colors"
              >
                {l.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <a
              data-testid="nav-resume-btn"
              href={profile.resume}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-1.5 bg-white text-black text-sm font-medium rounded-full px-4 py-2 hover:bg-neutral-200 transition-colors"
            >
              Resume
              <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />
            </a>
            <button
              data-testid="nav-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-full border border-[var(--border-subtle)] text-primary-c"
              aria-label="Toggle menu"
            >
              {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
          </div>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ x: "-50%", opacity: 0, y: -10 }}
            animate={{ x: "-50%", opacity: 1, y: 0 }}
            exit={{ x: "-50%", opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
            className="fixed top-20 left-1/2 z-40 w-[calc(100%-2rem)] max-w-4xl md:hidden"
          >
            <div className="rounded-3xl border border-[var(--border-subtle)] bg-surface-elevated backdrop-blur-xl p-4 shadow-2xl">
              {navLinks.map((l) => (
                <button
                  key={l.href}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  onClick={() => handleNav(l.href)}
                  className="block w-full text-left px-4 py-3 rounded-xl text-primary-c hover:bg-white/5 transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <a
                data-testid="mobile-resume-btn"
                href={profile.resume}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 flex items-center justify-center gap-1.5 bg-white text-black font-medium rounded-xl px-4 py-3"
              >
                Resume <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
